import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DeviceMobile, Lock, LockOpen, SignOut, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tijd = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "nu actief";
  if (min < 60) return `${min} min geleden`;
  return d.toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const SessieOverzicht = ({ isAdmin }) => {
  const [sessies, setSessies] = useState([]);
  const [laden, setLaden] = useState(true);
  const [bezig, setBezig] = useState("");

  const laad = useCallback(async () => {
    setLaden(true);
    try {
      const { data } = await axios.get(`${API}/sessies`);
      setSessies(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon sessies niet ophalen");
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const beeindig = async (s) => {
    setBezig(s.id);
    try {
      await axios.delete(`${API}/sessies/${s.id}`);
      toast.success("Sessie beëindigd");
      await laad();
    } catch (e) { toast.error(e.response?.data?.detail || "Beëindigen mislukt"); }
    finally { setBezig(""); }
  };

  const alleVanGebruiker = async (s) => {
    if (!window.confirm(`Alle apparaten van ${s.user_naam || s.user_email} uitloggen?`)) return;
    setBezig(s.id);
    try {
      const { data } = await axios.post(`${API}/sessies/gebruiker/${s.user_id}/uitloggen`);
      toast.success(data.message);
      await laad();
    } catch (e) { toast.error(e.response?.data?.detail || "Uitloggen mislukt"); }
    finally { setBezig(""); }
  };

  const zetSlot = async (s, actief) => {
    const vraag = actief
      ? `${s.user_naam || s.user_email} vergrendelen op "${s.device_naam}"? Inloggen vanaf andere apparaten wordt geblokkeerd.`
      : `Apparaatslot van ${s.user_naam || s.user_email} uitzetten?`;
    if (!window.confirm(vraag)) return;
    setBezig(s.id);
    try {
      const { data } = await axios.post(`${API}/sessies/gebruiker/${s.user_id}/apparaat-slot`, {
        actief,
        device_id: actief ? s.device_id : null,
        device_naam: actief ? s.device_naam : null,
      });
      toast.success(data.message);
      await laad();
    } catch (e) { toast.error(e.response?.data?.detail || "Vergrendelen mislukt"); }
    finally { setBezig(""); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-6" data-testid="sessies-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
            <DeviceMobile size={18} weight="bold" className="text-[#002FA7]" />
          </div>
          <div>
            <h3 className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Actieve sessies</h3>
            <p className="text-xs text-muted-foreground">
              {isAdmin ? "Wie is waar ingelogd — beëindig sessies of vergrendel iemand op één apparaat" : "Jouw apparaten die nu ingelogd zijn"}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={laad} className="rounded-sm h-9 shrink-0" data-testid="sessies-refresh-btn">
          <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Verversen
        </Button>
      </div>

      {laden ? (
        <p className="text-sm text-muted-foreground">Laden...</p>
      ) : sessies.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="sessies-leeg">Geen actieve sessies</p>
      ) : (
        <div className="space-y-2">
          {sessies.map((s) => (
            <div key={s.id} className="border border-border rounded-sm p-3" data-testid={`sessie-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">
                    {s.user_naam || s.user_email}
                    {s.dit_apparaat && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#38A169]">dit apparaat</span>
                    )}
                    {s.apparaat_vergrendeld && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#C62828]" data-testid={`slot-badge-${s.user_id}`}>
                        vergrendeld
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{s.user_email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.device_naam} · IP {s.ip || "onbekend"} · {tijd(s.last_seen)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={bezig === s.id}
                    onClick={() => beeindig(s)}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`sessie-uitloggen-${s.id}`}
                  >
                    <SignOut size={13} weight="bold" className="mr-1" /> Uitloggen
                  </Button>
                  {isAdmin && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={bezig === s.id}
                        onClick={() => alleVanGebruiker(s)}
                        className="rounded-sm h-8 text-xs"
                        data-testid={`sessie-alles-${s.user_id}`}
                      >
                        Alle apparaten
                      </Button>
                      {s.apparaat_vergrendeld ? (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={bezig === s.id}
                          onClick={() => zetSlot(s, false)}
                          className="rounded-sm h-8 text-xs"
                          data-testid={`sessie-ontgrendel-${s.user_id}`}
                        >
                          <LockOpen size={13} weight="bold" className="mr-1" /> Ontgrendel
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          disabled={bezig === s.id}
                          onClick={() => zetSlot(s, true)}
                          className="rounded-sm h-8 text-xs bg-[#C62828] hover:bg-[#A31F1F] text-white"
                          data-testid={`sessie-vergrendel-${s.user_id}`}
                        >
                          <Lock size={13} weight="bold" className="mr-1" /> Vergrendel op dit apparaat
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isAdmin && (
        <p className="text-[11px] text-muted-foreground mt-3">
          Vergrendelen betekent: deze gebruiker kan alleen nog inloggen op dát apparaat. Sessies op andere
          apparaten worden meteen beëindigd.
        </p>
      )}
    </div>
  );
};
